import {
  AdjustmentsHorizontalIcon,
  ArchiveBoxIcon,
  ArrowLeftIcon,
  CheckIcon,
  ClockIcon,
  CloudArrowUpIcon,
  ComputerDesktopIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import { UserGuard } from '@reader-tracker/shared/core/components/UserGuard'
import { supabase } from '@reader-tracker/shared/lib/supabase'
import { useAuthStore } from '@reader-tracker/shared/stores/authStore'
import { useCallback, useEffect, useState } from 'react'
import type { FormEvent, ReactElement } from 'react'
import { Link } from 'react-router-dom'
import { ClearAllConfirm } from '../Habit/components/ClearAllConfirm'
import { CsvImportSheet } from '../Habit/components/CsvImportSheet'
import { DeviceModeToggle } from '../Habit/components/DeviceModeToggle'
import type { Habit } from '../Habit/interfaces'
import { removeHabitById } from '../Habit/utils/habitArchive'
import { normalizeHabitCategory } from '../Habit/utils/habitCategories'
import { useDeviceMode } from '../Habit/utils/useDeviceMode'

type SettingsHabit = Pick<Habit, 'id' | 'name' | 'category'>

function normalizeSettingsHabit(habit: SettingsHabit): SettingsHabit {
  return {
    ...habit,
    category: normalizeHabitCategory(habit.category),
  }
}

function SettingsContent(): ReactElement {
  const { mode, setMode, isDesktop } = useDeviceMode()
  const user = useAuthStore((state) => state.user)
  const profile = useAuthStore((state) => state.profile)
  const updateDayStartTime = useAuthStore((state) => state.updateDayStartTime)

  const initialDayStart = (profile?.day_start_time ?? '00:00:00').slice(0, 5)
  const [dayStartTimeValue, setDayStartTimeValue] = useState(initialDayStart)
  const [savingTime, setSavingTime] = useState(false)
  const [timeSavedSuccess, setTimeSavedSuccess] = useState(false)
  const [timeErrorMsg, setTimeErrorMsg] = useState('')

  const [activeHabits, setActiveHabits] = useState<SettingsHabit[]>([])
  const [archivedHabits, setArchivedHabits] = useState<SettingsHabit[]>([])
  const [loadingHabits, setLoadingHabits] = useState(false)
  const [archiveErrorMsg, setArchiveErrorMsg] = useState('')
  const [archivingHabitId, setArchivingHabitId] = useState<string | null>(null)
  const [unarchivingHabitId, setUnarchivingHabitId] = useState<string | null>(
    null,
  )

  const [showImport, setShowImport] = useState(false)
  const [clearConfirmStep, setClearConfirmStep] = useState<0 | 1 | 2>(0)
  const [clearing, setClearing] = useState(false)
  const [clearSuccessMsg, setClearSuccessMsg] = useState('')

  useEffect(() => {
    if (profile?.day_start_time) {
      setDayStartTimeValue(profile.day_start_time.slice(0, 5))
    }
  }, [profile?.day_start_time])

  const fetchHabitLists = useCallback(async (): Promise<void> => {
    if (!user) return
    setLoadingHabits(true)
    setArchiveErrorMsg('')

    const [activeResult, archivedResult] = await Promise.all([
      supabase
        .schema('habit')
        .from('habits')
        .select('id, name, category')
        .eq('user_id', user.id)
        .eq('archived', false)
        .order('pinned', { ascending: false })
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true }),
      supabase
        .schema('habit')
        .from('habits')
        .select('id, name, category')
        .eq('user_id', user.id)
        .eq('archived', true)
        .order('updated_at', { ascending: false }),
    ])

    if (activeResult.error || archivedResult.error) {
      setArchiveErrorMsg(
        activeResult.error?.message ??
          archivedResult.error?.message ??
          'Unable to load habits list.',
      )
    } else {
      setActiveHabits(
        ((activeResult.data ?? []) as SettingsHabit[]).map(
          normalizeSettingsHabit,
        ),
      )
      setArchivedHabits(
        ((archivedResult.data ?? []) as SettingsHabit[]).map(
          normalizeSettingsHabit,
        ),
      )
    }

    setLoadingHabits(false)
  }, [user])

  useEffect(() => {
    if (!user) return
    fetchHabitLists()
  }, [user, fetchHabitLists])

  async function handleSaveDayStart(event: FormEvent): Promise<void> {
    event.preventDefault()
    setSavingTime(true)
    setTimeErrorMsg('')
    setTimeSavedSuccess(false)

    const { error } = await updateDayStartTime(`${dayStartTimeValue}:00`)
    setSavingTime(false)

    if (error) {
      setTimeErrorMsg(error)
    } else {
      setTimeSavedSuccess(true)
      setTimeout(() => setTimeSavedSuccess(false), 3000)
    }
  }

  async function handleArchive(habitId: string): Promise<void> {
    const habit = activeHabits.find((h) => h.id === habitId)
    setArchiveErrorMsg('')
    setArchivingHabitId(habitId)

    const { error } = await supabase
      .schema('habit')
      .from('habits')
      .update({ archived: true })
      .eq('id', habitId)

    setArchivingHabitId(null)

    if (error) {
      setArchiveErrorMsg(error.message)
      return
    }

    setActiveHabits((prev) => removeHabitById(prev, habitId))
    if (habit) {
      setArchivedHabits((prev) => [normalizeSettingsHabit(habit), ...prev])
    }
  }

  async function handleUnarchive(habitId: string): Promise<void> {
    const habit = archivedHabits.find((h) => h.id === habitId)
    setArchiveErrorMsg('')
    setUnarchivingHabitId(habitId)

    const { error } = await supabase
      .schema('habit')
      .from('habits')
      .update({ archived: false })
      .eq('id', habitId)

    setUnarchivingHabitId(null)

    if (error) {
      setArchiveErrorMsg(error.message)
      return
    }

    setArchivedHabits((prev) => removeHabitById(prev, habitId))
    if (habit) {
      setActiveHabits((prev) => [...prev, normalizeSettingsHabit(habit)])
    }
  }

  async function handleClearAllData(): Promise<void> {
    if (!user) return

    setClearing(true)
    setClearSuccessMsg('')

    const { data: userHabits } = await supabase
      .schema('habit')
      .from('habits')
      .select('id')
      .eq('user_id', user.id)

    const habitIds = (userHabits ?? []).map((h: { id: string }) => h.id)
    if (habitIds.length > 0) {
      await supabase
        .schema('habit')
        .from('habit_logs')
        .delete()
        .in('habit_id', habitIds)
      await supabase
        .schema('habit')
        .from('habits')
        .delete()
        .eq('user_id', user.id)
    }

    setActiveHabits([])
    setArchivedHabits([])
    setClearing(false)
    setClearConfirmStep(0)
    setClearSuccessMsg('All habits and tracking logs have been deleted.')
    setTimeout(() => setClearSuccessMsg(''), 5000)
  }

  return (
    <div
      className={`mx-auto px-4 transition-all duration-200 ${
        isDesktop ? 'max-w-4xl py-6 pb-20' : 'max-w-lg pt-4 pb-safe-20'
      }`}
    >
      {/* Top Header */}
      <div className="mb-6 flex flex-col gap-2">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 font-medium text-gray-500 text-sm hover:text-gray-900"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to Habits
        </Link>
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-100 text-indigo-700">
            <AdjustmentsHorizontalIcon className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-bold text-2xl text-gray-900">Settings</h1>
            <p className="text-gray-500 text-xs">
              Manage your schedule, layout preferences, habit archives, and
              data.
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-6">
        {/* 1. Daily Schedule */}
        <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-xs">
          <div className="mb-4 flex items-center gap-2.5 border-gray-100 border-b pb-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
              <ClockIcon className="h-4 w-4" />
            </div>
            <div>
              <h2 className="font-semibold text-base text-gray-900">
                Daily Schedule
              </h2>
              <p className="text-gray-500 text-xs">
                Configure your day cutoff time.
              </p>
            </div>
          </div>

          <form
            onSubmit={handleSaveDayStart}
            className="flex flex-col gap-4 sm:flex-row sm:items-end"
          >
            <div className="flex-1">
              <label
                htmlFor="day-start-time"
                className="mb-1 block font-medium text-gray-700 text-sm"
              >
                Day starts at
              </label>
              <p className="mb-2 text-gray-500 text-xs">
                Habits completed before this hour are counted toward the
                previous calendar day.
              </p>
              <input
                id="day-start-time"
                type="time"
                value={dayStartTimeValue}
                onChange={(e) => setDayStartTimeValue(e.target.value)}
                className="w-full rounded-xl border border-gray-300 px-3.5 py-2.5 text-base focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:max-w-xs"
              />
            </div>
            <button
              type="submit"
              disabled={savingTime}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-5 py-2.5 font-semibold text-sm text-white shadow-xs hover:bg-indigo-700 disabled:opacity-50"
            >
              {savingTime ? (
                'Saving…'
              ) : timeSavedSuccess ? (
                <>
                  <CheckIcon className="h-4 w-4" /> Saved
                </>
              ) : (
                'Save Schedule'
              )}
            </button>
          </form>

          {timeErrorMsg && (
            <p className="mt-3 text-red-600 text-sm">{timeErrorMsg}</p>
          )}
        </section>

        {/* 2. Display Preferences */}
        <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-xs">
          <div className="mb-4 flex items-center gap-2.5 border-gray-100 border-b pb-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
              <ComputerDesktopIcon className="h-4 w-4" />
            </div>
            <div>
              <h2 className="font-semibold text-base text-gray-900">
                Display Mode
              </h2>
              <p className="text-gray-500 text-xs">
                Select your preferred device layout format.
              </p>
            </div>
          </div>

          <p className="mb-3 text-gray-600 text-sm">
            Choose whether to adapt automatically based on screen width, or
            force compact mobile phone or wide split-column desktop view.
          </p>

          <DeviceModeToggle mode={mode} onModeChange={setMode} />
        </section>

        {/* 3. Data Management */}
        <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-xs">
          <div className="mb-4 flex items-center gap-2.5 border-gray-100 border-b pb-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
              <CloudArrowUpIcon className="h-4 w-4" />
            </div>
            <div>
              <h2 className="font-semibold text-base text-gray-900">
                Data Management
              </h2>
              <p className="text-gray-500 text-xs">
                Import habits from CSV or reset account data.
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-4 divide-y divide-gray-100">
            {/* Import CSV */}
            <div className="flex flex-col justify-between gap-3 pt-2 sm:flex-row sm:items-center">
              <div>
                <h3 className="font-semibold text-gray-900 text-sm">
                  Import CSV
                </h3>
                <p className="text-gray-500 text-xs">
                  Bulk import habits and historical completion records from a
                  spreadsheet.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowImport(true)}
                className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2 font-medium text-gray-700 text-sm shadow-xs hover:bg-gray-50"
              >
                <CloudArrowUpIcon className="h-4 w-4 text-gray-500" />
                Import CSV
              </button>
            </div>

            {/* Clear All Data */}
            <div className="flex flex-col justify-between gap-3 pt-4 sm:flex-row sm:items-center">
              <div>
                <h3 className="font-semibold text-red-600 text-sm">
                  Clear All Habits & Logs
                </h3>
                <p className="text-gray-500 text-xs">
                  Permanently delete all active habits, archives, and past
                  tracking history.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setClearConfirmStep(1)}
                className="inline-flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-2 font-medium text-red-600 text-sm shadow-xs hover:bg-red-100"
              >
                <TrashIcon className="h-4 w-4" />
                Clear All Data
              </button>
            </div>
          </div>

          {clearSuccessMsg && (
            <p className="mt-3 font-medium text-emerald-700 text-sm">
              {clearSuccessMsg}
            </p>
          )}
        </section>

        {/* 4. Habit Archives */}
        <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-xs">
          <div className="mb-4 flex items-center gap-2.5 border-gray-100 border-b pb-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
              <ArchiveBoxIcon className="h-4 w-4" />
            </div>
            <div>
              <h2 className="font-semibold text-base text-gray-900">
                Habit Archives
              </h2>
              <p className="text-gray-500 text-xs">
                Archive habits to hide them from the daily tracker without
                losing history.
              </p>
            </div>
          </div>

          {archiveErrorMsg && (
            <p className="mb-4 text-red-600 text-sm">{archiveErrorMsg}</p>
          )}

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* Active Habits */}
            <div>
              <div className="mb-2 flex items-center justify-between">
                <h3 className="font-medium text-gray-900 text-sm">
                  Active Habits
                </h3>
                <span className="rounded-full bg-gray-100 px-2 py-0.5 font-semibold text-gray-600 text-xs">
                  {activeHabits.length}
                </span>
              </div>

              {loadingHabits ? (
                <div className="flex flex-col gap-2">
                  {[1, 2].map((i) => (
                    <div
                      key={i}
                      className="h-12 animate-pulse rounded-xl bg-gray-100"
                    />
                  ))}
                </div>
              ) : activeHabits.length === 0 ? (
                <p className="rounded-xl border border-gray-200 border-dashed px-3 py-4 text-center text-gray-400 text-xs">
                  No active habits.
                </p>
              ) : (
                <div className="flex max-h-72 flex-col gap-2 overflow-y-auto pr-1">
                  {activeHabits.map((habit) => (
                    <div
                      key={habit.id}
                      className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-gray-50/50 px-3 py-2"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium text-gray-900 text-sm">
                          {habit.name}
                        </p>
                        <span className="inline-block rounded-md bg-gray-200/70 px-1.5 py-0.5 font-medium text-[10px] text-gray-600">
                          {habit.category}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleArchive(habit.id)}
                        disabled={archivingHabitId === habit.id}
                        className="shrink-0 rounded-lg bg-amber-50 px-2.5 py-1 font-medium text-amber-700 text-xs hover:bg-amber-100 disabled:opacity-50"
                      >
                        {archivingHabitId === habit.id
                          ? 'Archiving…'
                          : 'Archive'}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Archived Habits */}
            <div>
              <div className="mb-2 flex items-center justify-between">
                <h3 className="font-medium text-gray-900 text-sm">
                  Archived Habits
                </h3>
                <span className="rounded-full bg-gray-100 px-2 py-0.5 font-semibold text-gray-600 text-xs">
                  {archivedHabits.length}
                </span>
              </div>

              {loadingHabits ? (
                <div className="flex flex-col gap-2">
                  {[1, 2].map((i) => (
                    <div
                      key={i}
                      className="h-12 animate-pulse rounded-xl bg-gray-100"
                    />
                  ))}
                </div>
              ) : archivedHabits.length === 0 ? (
                <p className="rounded-xl border border-gray-200 border-dashed px-3 py-4 text-center text-gray-400 text-xs">
                  No archived habits.
                </p>
              ) : (
                <div className="flex max-h-72 flex-col gap-2 overflow-y-auto pr-1">
                  {archivedHabits.map((habit) => (
                    <div
                      key={habit.id}
                      className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-gray-50/50 px-3 py-2"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium text-gray-900 text-sm">
                          {habit.name}
                        </p>
                        <span className="inline-block rounded-md bg-gray-200/70 px-1.5 py-0.5 font-medium text-[10px] text-gray-600">
                          {habit.category}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleUnarchive(habit.id)}
                        disabled={unarchivingHabitId === habit.id}
                        className="shrink-0 rounded-lg bg-gray-200 px-2.5 py-1 font-medium text-gray-700 text-xs hover:bg-gray-300 disabled:opacity-50"
                      >
                        {unarchivingHabitId === habit.id
                          ? 'Restoring…'
                          : 'Restore'}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      </div>

      {/* CSV Import Sheet */}
      {showImport && user && (
        <CsvImportSheet
          userId={user.id}
          onClose={() => setShowImport(false)}
          onImportComplete={() => {
            setShowImport(false)
            fetchHabitLists()
          }}
        />
      )}

      {/* Clear All Confirmation Modal */}
      {clearConfirmStep > 0 && (
        <ClearAllConfirm
          step={clearConfirmStep as 1 | 2}
          onNext={() => setClearConfirmStep(2)}
          onCancel={() => setClearConfirmStep(0)}
          onConfirm={handleClearAllData}
          clearing={clearing}
        />
      )}
    </div>
  )
}

export default function SettingsPage(): ReactElement {
  return (
    <UserGuard>
      <div className="min-h-screen bg-gray-50">
        <SettingsContent />
      </div>
    </UserGuard>
  )
}
